import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Settings, Plus, Trash2, ArrowLeft, Users, Building2, Hospital, Truck, History, ChevronLeft, ChevronRight, Printer, Download } from "lucide-react";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ title }) {
  return (
    <div style={{
      background: "#00BFFF", color: "#FFF", padding: "6px 12px",
      fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 13,
      textTransform: "uppercase", textAlign: "center",
    }}>
      {title}
    </div>
  );
}

function AdminSection({ title, icon, children }) {
  return (
    <div style={{ border: "1px solid #333", marginBottom: 16 }}>
      <SectionHeader title={title} />
      <div style={{ padding: 12 }}>{children}</div>
    </div>
  );
}

function AddRow({ placeholder, onAdd, testId }) {
  const [val, setVal] = useState("");
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <input
        data-testid={`${testId}-input`}
        type="text" value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onAdd(val.trim()); setVal(""); } }}
        placeholder={placeholder}
        style={{ flex: 1, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }}
      />
      <button data-testid={`${testId}-btn`}
        onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}
        style={{ background: "#00BFFF", color: "#FFF", border: "none", padding: "8px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
        <Plus size={12} /> Add
      </button>
    </div>
  );
}

function ItemRow({ name, detail, onDelete, testId }) {
  return (
    <div data-testid={testId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #222", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div>
        <span style={{ color: "#FFF" }}>{name}</span>
        {detail && <span style={{ color: "#888", marginLeft: 8, fontSize: 11 }}>{detail}</span>}
      </div>
      <button onClick={onDelete} style={{ background: "none", border: "1px solid #333", color: "#FF3333", padding: "2px 6px", cursor: "pointer", fontSize: 10, fontFamily: "'Manrope', sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
        <Trash2 size={10} /> Remove
      </button>
    </div>
  );
}

export default function AdminPanel({ user }) {
  // Permissions model:
  //   * AGENCY ADMIN / SUPERVISOR (from the Excel permissions column) and the
  //     built-in admin account have full add/edit/delete rights.
  //   * A user whose permissions are just DISPATCHER (or empty) can ONLY view
  //     Unit History — they are blocked from adding or editing Users,
  //     Agencies, Hospitals, Call Types, and KML boundaries.
  const perms = (user?.permissions || []).map((p) => (p || "").toUpperCase());
  const isBuiltInAdmin = user?.dispatcher_userid === "admin";
  const hasAdminPerm = perms.some((p) => p === "AGENCY ADMIN" || p === "SUPERVISOR" || p === "ADMIN");
  const canEdit = isBuiltInAdmin || hasAdminPerm;
  const [activeTab, setActiveTab] = useState(canEdit ? "users" : "history");
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitHistory, setUnitHistory] = useState([]);

  // Hospital form
  const [hospName, setHospName] = useState("");
  const [hospAddress, setHospAddress] = useState("");
  const [hospBorough, setHospBorough] = useState("");

  // Unit form
  const [unitName, setUnitName] = useState("");
  const [unitNum, setUnitNum] = useState("");
  const [unitAgency, setUnitAgency] = useState("");

  // User form
  const [userName, setUserName] = useState("");
  const [userAgency, setUserAgency] = useState("");
  const [userUploadResult, setUserUploadResult] = useState(null);

  const handleUserExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Upload "${file.name}"?\n\nThis will REPLACE all existing users with the rows in this spreadsheet. The built-in 'admin' account is preserved.`
    );
    if (!ok) { e.target.value = ""; return; }
    setUserUploadResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/users/upload-excel?replace=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUserUploadResult({ success: true, added: res.data.added, replaced: res.data.replaced });
      fetchAll();
    } catch {
      setUserUploadResult({ success: false, error: "Upload failed — check the spreadsheet format" });
    }
    e.target.value = "";
  };

  const downloadUserTemplate = () => {
    window.location.href = `${API}/users/template?t=${Date.now()}`;
  };

  const fetchAll = async () => {
    try {
      const [ag, us, ho, un, uh] = await Promise.all([
        axios.get(`${API}/agencies`),
        axios.get(`${API}/users`),
        axios.get(`${API}/hospitals`),
        axios.get(`${API}/units`),
        axios.get(`${API}/units/history`),
      ]);
      // Iter 115 — scope the admin roster to the caller's own agency
      // (plus its sister). Super admins (no agency) see everyone. This
      // matches the AdminModals user list and keeps chiefs from
      // browsing another VAC's roster.
      const isSuperAdmin = !user?.agency || user.agency === "";
      const myAgencies = user?.agencies?.length ? user.agencies : (user?.agency ? [user.agency] : []);
      const visibleUsers = isSuperAdmin
        ? us.data
        : (us.data || []).filter((u) => {
            if (myAgencies.includes(u.agency)) return true;
            if (Array.isArray(u.agencies) && u.agencies.some((a) => myAgencies.includes(a))) return true;
            return false;
          });
      setAgencies(ag.data);
      setUsers(visibleUsers);
      setHospitals(ho.data);
      setUnits(un.data);
      setUnitHistory(uh.data);
    } catch {}
  };

  useEffect(() => { fetchAll(); }, []);

  const allTabs = [
    { id: "users", label: "Add User", icon: <Users size={14} />, editOnly: true },
    { id: "agencies", label: "Add Agency", icon: <Building2 size={14} />, editOnly: true },
    { id: "hospitals", label: "Add Hospital", icon: <Hospital size={14} />, editOnly: true },
    { id: "units", label: "Add Unit", icon: <Truck size={14} />, editOnly: true },
    { id: "history", label: "Unit History", icon: <History size={14} /> },
  ];
  const tabs = allTabs.filter((t) => canEdit || !t.editOnly);

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#FFF" }}>
      {/* Header */}
      <div style={{ background: "#111", borderBottom: "1px solid #333", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Settings size={20} color="#00BFFF" />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "#00BFFF", textTransform: "uppercase", letterSpacing: 1 }}>
            Admin Panel
          </span>
        </div>
        <Link to="/" style={{ color: "#888", fontSize: 12, textDecoration: "none", fontFamily: "'IBM Plex Sans', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
          <ArrowLeft size={12} /> Back to Dispatch
        </Link>
      </div>

      <div style={{ display: "flex", maxWidth: 1000, margin: "0 auto", padding: "20px" }}>
        {/* Sidebar Tabs */}
        <div style={{ width: 180, marginRight: 20 }}>
          {tabs.map((t) => (
            <div
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: "10px 12px",
                marginBottom: 4,
                cursor: "pointer",
                background: activeTab === t.id ? "#00BFFF" : "#111",
                color: activeTab === t.id ? "#FFF" : "#888",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: activeTab === t.id ? "1px solid #00BFFF" : "1px solid #222",
                transition: "all 0.15s",
              }}
            >
              {t.icon} {t.label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          {/* ADD USER */}
          {activeTab === "users" && (
            <AdminSection title="USERS / CREW MEMBERS">
              {/* ─── Bulk Import banner — prominent, replaces entire roster ─── */}
              <div style={{
                marginBottom: 14, padding: "12px 14px",
                background: "linear-gradient(135deg, #0A2A5A 0%, #1A4A8A 100%)",
                border: "1px solid #2A6ABA", borderRadius: 4,
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ color: "#FFF", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>
                    Bulk Import from Excel
                  </div>
                  <div style={{ color: "#A8C0E0", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, marginTop: 2 }}>
                    Uploading a spreadsheet replaces the current user list. The admin account is preserved.
                  </div>
                </div>
                <button
                  data-testid="admin-user-template-download"
                  type="button"
                  onClick={downloadUserTemplate}
                  style={{
                    background: "#1A3A6A", color: "#C8DCF8", border: "1px solid #3A6AAA",
                    padding: "8px 14px", fontFamily: "'Manrope', sans-serif", fontSize: 12,
                    fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  }}>
                  <Download size={12} /> Template
                </button>
                <label
                  data-testid="admin-user-excel-upload-label"
                  style={{
                    background: "linear-gradient(180deg, #52A85C, #2E7A3A)",
                    color: "#FFF", border: "1px solid #1E5A24",
                    padding: "8px 18px", fontFamily: "'Manrope', sans-serif", fontSize: 12,
                    fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  }}>
                  <Plus size={12} /> Upload Excel File
                  <input data-testid="admin-user-excel-upload" type="file"
                    accept=".xlsx,.xls,.csv" onChange={handleUserExcelUpload}
                    style={{ display: "none" }} />
                </label>
              </div>
              {userUploadResult && (
                <div data-testid="admin-user-upload-result" style={{
                  marginBottom: 12, padding: "6px 12px", borderRadius: 3, fontSize: 12,
                  fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600,
                  background: userUploadResult.success ? "#0E3A14" : "#3A0E0E",
                  border: `1px solid ${userUploadResult.success ? "#2A8A3A" : "#AA2A2A"}`,
                  color: userUploadResult.success ? "#9EE8A8" : "#FFB0B0",
                }}>
                  {userUploadResult.success
                    ? `Roster replaced — ${userUploadResult.replaced || 0} removed, ${userUploadResult.added} imported.`
                    : userUploadResult.error}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input data-testid="admin-user-name" type="text" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Crew member name..." style={{ flex: 1, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <select data-testid="admin-user-agency" value={userAgency} onChange={(e) => setUserAgency(e.target.value)} style={{ background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <option value="">Agency...</option>
                  {agencies.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                <button data-testid="admin-user-add" onClick={async () => { if (userName.trim()) { await axios.post(`${API}/users`, { name: userName.trim(), role: "crew", agency: userAgency }); setUserName(""); setUserAgency(""); fetchAll(); } }}
                  style={{ background: "#00BFFF", color: "#FFF", border: "none", padding: "8px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {users.map((u) => (
                <ItemRow key={u.id} name={u.name} detail={u.agency} testId={`user-${u.name}`}
                  onDelete={async () => { await axios.delete(`${API}/users/${u.id}`); fetchAll(); }} />
              ))}
              {users.length === 0 && <div style={{ color: "#666", fontSize: 13, padding: 8 }}>No users added yet</div>}
            </AdminSection>
          )}

          {/* ADD AGENCY */}
          {activeTab === "agencies" && (
            <AdminSection title="AGENCIES">
              <AddRow placeholder="New agency name..." testId="admin-agency" onAdd={async (name) => { await axios.post(`${API}/agencies`, { name }); fetchAll(); }} />
              {agencies.map((a) => (
                <ItemRow key={a.id} name={a.name} testId={`agency-${a.name}`}
                  onDelete={async () => { await axios.delete(`${API}/agencies/${a.id}`); fetchAll(); }} />
              ))}
              {agencies.length === 0 && <div style={{ color: "#666", fontSize: 13, padding: 8 }}>No agencies added yet</div>}
            </AdminSection>
          )}

          {/* ADD HOSPITAL */}
          {activeTab === "hospitals" && (
            <AdminSection title="HOSPITALS">
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <input data-testid="admin-hosp-name" type="text" value={hospName} onChange={(e) => setHospName(e.target.value)} placeholder="Hospital name..." style={{ flex: 2, minWidth: 150, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <input data-testid="admin-hosp-address" type="text" value={hospAddress} onChange={(e) => setHospAddress(e.target.value)} placeholder="Address..." style={{ flex: 2, minWidth: 150, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <input data-testid="admin-hosp-borough" type="text" value={hospBorough} onChange={(e) => setHospBorough(e.target.value)} placeholder="Borough..." style={{ flex: 1, minWidth: 100, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <button data-testid="admin-hosp-add" onClick={async () => { if (hospName.trim()) { await axios.post(`${API}/hospitals`, { name: hospName.trim(), address: hospAddress.trim(), borough: hospBorough.trim() }); setHospName(""); setHospAddress(""); setHospBorough(""); fetchAll(); } }}
                  style={{ background: "#00BFFF", color: "#FFF", border: "none", padding: "8px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {hospitals.map((h) => (
                <ItemRow key={h.id} name={h.name} detail={`${h.address || ""} ${h.borough ? `(${h.borough})` : ""}`} testId={`hospital-${h.name}`}
                  onDelete={async () => { await axios.delete(`${API}/hospitals/${h.id}`); fetchAll(); }} />
              ))}
              {hospitals.length === 0 && <div style={{ color: "#666", fontSize: 13, padding: 8 }}>No hospitals added yet</div>}
            </AdminSection>
          )}

          {/* ADD UNIT */}
          {activeTab === "units" && (
            <AdminSection title="UNITS">
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input data-testid="admin-unit-name" type="text" value={unitName} onChange={(e) => setUnitName(e.target.value)} placeholder="Unit name (e.g. BSVAC-7)..." style={{ flex: 1, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px 12px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <input data-testid="admin-unit-num" type="number" value={unitNum} onChange={(e) => setUnitNum(e.target.value)} placeholder="#" style={{ width: 60, background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif", outline: "none" }} />
                <select data-testid="admin-unit-agency" value={unitAgency} onChange={(e) => setUnitAgency(e.target.value)} style={{ background: "#000", color: "#FFF", border: "1px solid #333", padding: "8px", fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <option value="">Agency...</option>
                  {agencies.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                <button data-testid="admin-unit-add" onClick={async () => { if (unitName.trim() && unitAgency) { await axios.post(`${API}/units`, { name: unitName.trim(), unit_number: parseInt(unitNum) || 0, agency: unitAgency }); setUnitName(""); setUnitNum(""); setUnitAgency(""); fetchAll(); } }}
                  style={{ background: "#00BFFF", color: "#FFF", border: "none", padding: "8px 14px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={12} /> Add
                </button>
              </div>
              {units.map((u) => (
                <ItemRow key={u.id} name={u.name} detail={`#${u.unit_number} — ${u.agency} — ${u.status}`} testId={`unit-row-${u.name}`}
                  onDelete={async () => { await axios.delete(`${API}/units/${u.id}`).catch(() => {}); fetchAll(); }} />
              ))}
              {units.length === 0 && <div style={{ color: "#666", fontSize: 13, padding: 8 }}>No units configured</div>}
            </AdminSection>
          )}

          {/* UNIT HISTORY - calendar + session picker + PDF export */}
          {activeTab === "history" && <UnitHistoryTab />}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   UNIT HISTORY TAB — calendar + session picker + PDF/print
   ═══════════════════════════════════════════════════════ */
function UnitHistoryTab() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [dateSet, setDateSet] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null); // {unit_id, unit_name, tour}
  const [entries, setEntries] = useState([]);

  // Load the shaded days for the current month.
  useEffect(() => {
    axios.get(`${API}/admin/unit-history/calendar`, { params: { year, month: month + 1 } })
      .then((r) => setDateSet(new Set(r.data.dates || [])))
      .catch(() => setDateSet(new Set()));
    setSelectedDate(null);
    setSessions([]);
    setSelectedSession(null);
    setEntries([]);
  }, [year, month]);

  // Load sessions when a date is clicked.
  useEffect(() => {
    if (!selectedDate) return;
    axios.get(`${API}/admin/unit-history/by-date`, { params: { date: selectedDate } })
      .then((r) => setSessions(r.data.sessions || []))
      .catch(() => setSessions([]));
    setSelectedSession(null);
    setEntries([]);
  }, [selectedDate]);

  // Load entries when a session is picked.
  useEffect(() => {
    if (!selectedSession) return;
    axios.get(`${API}/admin/unit-history/session`, {
      params: { date: selectedDate, unit_id: selectedSession.unit_id, tour: selectedSession.tour || "" },
    }).then((r) => setEntries(r.data.entries || [])).catch(() => setEntries([]));
  }, [selectedSession, selectedDate]);

  const monthLabel = useMemo(() => new Date(year, month, 1).toLocaleString("en-US", { month: "long", year: "numeric" }), [year, month]);

  // Build calendar grid (7 cols). First day offset padded, trailing blanks omitted.
  const grid = useMemo(() => {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = Array(firstDow).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const goPrev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const goNext = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const fmtDateStr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const prettyDate = selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "";
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";

  // ── Print: new window with a clean printable layout.
  const printSession = () => {
    if (!selectedSession || entries.length === 0) return;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const rows = entries.map((e) => `<tr>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;font-family:monospace;">${fmtTime(e.timestamp)}</td>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;font-weight:600;">${(e.event || "").toUpperCase()}</td>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;">${(e.details || "").replace(/</g, "&lt;")}</td>
    </tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>Unit History — ${selectedSession.unit_name} — ${selectedDate}</title>
      <style>body{font-family:Arial,sans-serif;margin:24px;color:#000}h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:0 0 12px;color:#444;font-weight:400}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#00BFFF;color:#FFF;padding:5px 8px;text-align:left}</style>
      </head><body>
      <h1>UNIT HISTORY — ${selectedSession.unit_name}</h1>
      <h2>${prettyDate} · Tour: ${selectedSession.tour || "—"} · ${entries.length} entries</h2>
      <table><thead><tr><th>Time</th><th>Event</th><th>Details</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
      </body></html>`);
    w.document.close();
  };

  // ── PDF download via jsPDF.
  const downloadPdf = () => {
    if (!selectedSession || entries.length === 0) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const title = `UNIT HISTORY — ${selectedSession.unit_name}`;
    const subtitle = `${prettyDate}   Tour: ${selectedSession.tour || "—"}   (${entries.length} entries)`;
    doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text(title, 40, 50);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(80); doc.text(subtitle, 40, 68);
    doc.setTextColor(0);
    // Header
    let y = 96;
    doc.setFillColor(0, 191, 255); doc.rect(40, y - 14, 532, 20, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("Time", 48, y); doc.text("Event", 130, y); doc.text("Details", 230, y);
    doc.setTextColor(0); doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    y += 18;
    for (const e of entries) {
      if (y > 740) { doc.addPage(); y = 60; }
      const time = fmtTime(e.timestamp);
      const ev = (e.event || "").toUpperCase();
      const lines = doc.splitTextToSize(e.details || "", 340);
      doc.text(time, 48, y);
      doc.text(ev, 130, y);
      doc.text(lines, 230, y);
      y += Math.max(14, lines.length * 12);
      doc.setDrawColor(220); doc.line(40, y - 6, 572, y - 6);
    }
    doc.save(`UnitHistory_${selectedSession.unit_name}_${selectedDate}_${(selectedSession.tour || "").replace(/\s+/g, "_") || "NA"}.pdf`);
  };

  const btnBase = { background: "#222", border: "1px solid #333", color: "#FFF", padding: "6px 10px", cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 };

  return (
    <AdminSection title="UNIT HISTORY">
      <div style={{ display: "grid", gridTemplateColumns: "360px 300px 1fr", gap: 14, alignItems: "start" }}>
        {/* Calendar */}
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 6, padding: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button data-testid="unit-hist-prev-month" onClick={goPrev} style={{ ...btnBase, padding: "4px 8px" }}><ChevronLeft size={14} /></button>
            <div style={{ color: "#00BFFF", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>{monthLabel}</div>
            <button data-testid="unit-hist-next-month" onClick={goNext} style={{ ...btnBase, padding: "4px 8px" }}><ChevronRight size={14} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 10, color: "#666", marginBottom: 4, textAlign: "center" }}>
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = fmtDateStr(d);
              const hasData = dateSet.has(key);
              const isSel = selectedDate === key;
              return (
                <div key={i}
                  data-testid={`unit-hist-day-${key}`}
                  onClick={hasData ? () => setSelectedDate(key) : undefined}
                  style={{
                    padding: "8px 0", textAlign: "center", fontSize: 13, borderRadius: 3,
                    background: isSel ? "#00BFFF" : hasData ? "#BEE5FF" : "#1a1a1a",
                    color: isSel ? "#000" : hasData ? "#003366" : "#555",
                    cursor: hasData ? "pointer" : "default",
                    fontWeight: hasData ? 700 : 400,
                  }}>{d}</div>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: "#888", marginTop: 10 }}>Dates with archived unit history are shaded light blue.</div>
        </div>

        {/* Sessions list */}
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 6, padding: 10, minHeight: 280 }}>
          <div style={{ color: "#00BFFF", fontWeight: 700, textTransform: "uppercase", fontSize: 12, marginBottom: 8 }}>
            {selectedDate ? `Sessions · ${prettyDate}` : "Select a date"}
          </div>
          {selectedDate && sessions.length === 0 && <div style={{ color: "#666", fontSize: 12 }}>No sessions on this date.</div>}
          {sessions.map((s) => {
            const isSel = selectedSession && selectedSession.unit_id === s.unit_id && selectedSession.tour === s.tour;
            return (
              <div
                key={`${s.unit_id}-${s.tour}`}
                data-testid={`unit-hist-session-${s.unit_name}-${s.tour || "na"}`}
                onClick={() => setSelectedSession(s)}
                style={{
                  padding: "8px 10px", marginBottom: 4, borderRadius: 3, cursor: "pointer",
                  background: isSel ? "#00BFFF" : "#181818",
                  color: isSel ? "#000" : "#FFF",
                  border: "1px solid " + (isSel ? "#00BFFF" : "#222"),
                }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{s.unit_name}</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>{s.tour || "No tour"} · {s.entries} events</div>
              </div>
            );
          })}
        </div>

        {/* Detail + actions */}
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 6, padding: 10, minHeight: 280 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ color: "#00BFFF", fontWeight: 700, textTransform: "uppercase", fontSize: 12 }}>
              {selectedSession ? `${selectedSession.unit_name} · ${selectedSession.tour || "No tour"}` : "Select a session"}
            </div>
            {selectedSession && (
              <div style={{ display: "flex", gap: 6 }}>
                <button data-testid="unit-hist-print" onClick={printSession} style={btnBase}><Printer size={14} /> Print</button>
                <button data-testid="unit-hist-pdf" onClick={downloadPdf} style={{ ...btnBase, background: "#00BFFF", color: "#000", border: "1px solid #00BFFF" }}><Download size={14} /> PDF</button>
              </div>
            )}
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
            {entries.length === 0 && selectedSession && <div style={{ color: "#666" }}>No entries.</div>}
            {entries.map((e, i) => (
              <div key={e.id || i} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: "1px solid #222" }}>
                <span style={{ color: "#666", minWidth: 70 }}>{fmtTime(e.timestamp)}</span>
                <span style={{ color: "#FFAA00", minWidth: 120, fontWeight: 700 }}>{(e.event || "").toUpperCase()}</span>
                <span style={{ color: "#FFF", flex: 1 }}>{e.details}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminSection>
  );
}

